// Stitches the on-screen figures (held in a ref-backed registry, keyed by canvas
// card) onto the structural view, at the moment a question is sent.
//
// The two halves are collected separately on purpose: the structure is reactive
// and small, the values change on every gadget refetch and are large, so they're
// pulled imperatively here rather than pushed through React state. This is also
// where `cardId` — a client-only join key — is dropped, so it never goes on the
// wire.

import {
  InsightsGadgetValues,
  InsightsView,
  InsightsViewGadget,
  InsightsViewValues,
} from 'contextProviders/InsightsContentProvider/viewTypes';

// Only the first few gadgets get their numbers sent. A 12-gadget report's worth
// of raw rows would both blow the token budget and dilute the model's attention
// across a wall of digits.
export const MAX_VALUE_GADGETS = 4;

const isLoadingMarker = (
  values: InsightsGadgetValues,
): values is { isLoading: true } =>
  !!values && 'isLoading' in values && values.isLoading === true;

const hasFigures = (
  values: InsightsGadgetValues,
): values is InsightsViewValues => !!values && !isLoadingMarker(values);

const mergeValuesIntoView = ({
  view,
  valuesByCardId,
  priorityCardId,
}: {
  view: InsightsView;
  valuesByCardId: { [cardId: string]: InsightsGadgetValues };
  // The gadget the user pointed at ("Explain this value"). It always gets its
  // figures: a slot is held back for it so the cap can't mark it 'truncated'
  // when it sits below the first MAX_VALUE_GADGETS on the page.
  priorityCardId?: string;
}): InsightsView => {
  let valuesAttached = 0;
  // Hold a slot only while the priority gadget is still ahead of us AND actually
  // has figures to attach — otherwise the reservation would waste a slot.
  let isPrioritySlotReserved =
    !!priorityCardId &&
    view.gadgets.some((gadget) => gadget.cardId === priorityCardId) &&
    hasFigures(valuesByCardId[priorityCardId]);

  const gadgets = view.gadgets.map((gadget): InsightsViewGadget => {
    // Strip the join key — it means nothing to the model.
    const { cardId, ...rest } = gadget;
    const raw = cardId ? valuesByCardId[cardId] : undefined;
    const isPriority = !!cardId && cardId === priorityCardId;

    if (isLoadingMarker(raw)) {
      // Mid-refetch. Reporting the previous filters' numbers would be worse than
      // reporting none, so say why they're missing.
      return { ...rest, valuesUnavailableReason: 'loading' };
    }

    if (!raw) {
      // No getter registered, so this gadget's data resolves through neither
      // useV5ChartData nor useMetricMatrix. Remaining cases: simpleGrid (raw rows,
      // not aggregations), fixedTrendCard / rollingTrendCard, and the non-V5 vis
      // types (Gauge, RankingMatrix, PaceMatrix, SingleMetricDateMatrix,
      // GroupVsRemainder, EntityInformationTable, Reminders).
      //
      // Marked explicitly: silence would read to the model as "this gadget has no
      // data", which is a different and wrong claim.
      return { ...rest, valuesUnavailableReason: 'unsupported-gadget' };
    }

    const limit = isPriority
      ? Number.POSITIVE_INFINITY
      : MAX_VALUE_GADGETS - (isPrioritySlotReserved ? 1 : 0);
    if (valuesAttached >= limit) {
      return { ...rest, valuesUnavailableReason: 'truncated' };
    }

    if (isPriority) {
      isPrioritySlotReserved = false;
    }
    valuesAttached += 1;
    const values: InsightsViewValues = raw;
    return { ...rest, values };
  });

  return { ...view, gadgets };
};

export default mergeValuesIntoView;
